import { Play, Pause, RotateCcw, Volume2, Download } from "lucide-react";
import type React from "react";
import { useState, useRef, useEffect, useCallback } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Slider } from "@/app/_module/components/ui/slider";
import { AudioWaveform } from "@/app/_module/components/ui/audio-waveform";

interface VoicemailPlayerProps {
  audioUrl: string;
  duration: number;
  onPlayStateChange?: (isPlaying: boolean) => void;
  onComplete?: () => void;
  onMarkAsListened?: () => void;
  autoPlay?: boolean;
  showDownload?: boolean;
  transcript?: string;
}

export const VoicemailPlayer: React.FC<VoicemailPlayerProps> = function({
  audioUrl,
  duration,
  onPlayStateChange,
  onComplete,
  onMarkAsListened,
  autoPlay = false,
  showDownload = true,
  transcript
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [volume, setVolume] = useState(1);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [hasTriggeredListened, setHasTriggeredListened] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update state when audioUrl changes (reset everything for new voicemail)
  useEffect(() => {
    setAudioReady(false);
    setAudioError(null);
    setHasTriggeredListened(false);
    setIsPlaying(false);
    setCurrentTime(0);

    // If no audio URL, show error immediately instead of silent failure
    if (!audioUrl) {
      setLoading(false);
      setAudioError('No recording available for this voicemail');
    } else {
      // Don't set loading=true here — we load on user click, not eagerly
      setLoading(false);
      setAudioReady(true);
    }
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    // Set initial volume
    audio.volume = volume;

    const handleTimeUpdate = () => {
      if (audio.currentTime !== undefined) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleCanPlay = () => {
      setLoading(false);
      setAudioReady(true);
      setAudioError(null);
    };

    const handleLoadedMetadata = () => {
      setLoading(false);
      setAudioReady(true);
      setAudioError(null);
    };
    
    const handleError = (e: Event) => {
      const audioElement = e.target as HTMLAudioElement;
      const error = audioElement.error;
      let errorMessage = 'Unknown audio error';
      
      if (error) {
        switch (error.code) {
          case error.MEDIA_ERR_ABORTED:
            errorMessage = 'Audio loading aborted';
            break;
          case error.MEDIA_ERR_NETWORK:
            errorMessage = 'Network error loading audio';
            break;
          case error.MEDIA_ERR_DECODE:
            errorMessage = 'Audio decoding error';
            break;
          case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = 'Audio format not supported';
            break;
        }
      }

      setAudioError(errorMessage);
      setLoading(false);
      setAudioReady(true);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(audio.currentTime || 0);
      onPlayStateChange?.(false);
      onComplete?.();
    };

    const handlePause = () => {
      setIsPlaying(false);
      onPlayStateChange?.(false);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      onPlayStateChange?.(true);
    };

    const handleVolumeChange = () => {
      setVolume(audio.volume);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('volumechange', handleVolumeChange);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('volumechange', handleVolumeChange);
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [audioUrl, onPlayStateChange, onComplete, volume]);

  // Handle autoplay after component is ready
  useEffect(() => {
    if (autoPlay && audioReady && !loading) {
      const timer = setTimeout(() => {
        handlePlay();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoPlay, audioReady, loading]);

  const handlePlay = async () => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) {
      return;
    }

    try {
      if (isPlaying) {
        audio.pause();
      } else {
        // Ensure volume is audible
        if (audio.volume === 0) {
          audio.volume = 0.8;
          setVolume(0.8);
        }

        // Load the audio on first play (user gesture satisfies browser autoplay policy)
        if (!audio.src || audio.readyState === 0) {
          setLoading(true);
          // Set the src only now (not on the element) to avoid premature loadstart events
          if (!audio.src || !audio.src.includes(audioUrl)) {
            audio.src = audioUrl;
          }
          audio.load();
          await new Promise<void>((resolve, reject) => {
            const onCanPlay = () => {
              audio.removeEventListener('canplay', onCanPlay);
              audio.removeEventListener('error', onError);
              setLoading(false);
              resolve();
            };
            const onError = () => {
              audio.removeEventListener('canplay', onCanPlay);
              audio.removeEventListener('error', onError);
              setLoading(false);
              reject(new Error('Failed to load audio'));
            };
            audio.addEventListener('canplay', onCanPlay, { once: true });
            audio.addEventListener('error', onError, { once: true });
          });
        }

        // Ensure we're at the right position
        if (currentTime > 0 && Math.abs(audio.currentTime - currentTime) > 1) {
          audio.currentTime = currentTime;
        }

        await audio.play();

        // Mark as listened on first play
        if (!hasTriggeredListened && onMarkAsListened) {
          setHasTriggeredListened(true);
          onMarkAsListened();
        }
      }
    } catch (error) {
      // Set error state
      setAudioError('Playback failed. Try again.');
      setIsPlaying(false);
      setLoading(false);
      onPlayStateChange?.(false);
    }
  };

  const handleSeek = (value: number[]) => {
    const seekTime = (value[0] / 100) * duration;
    
    const audio = audioRef.current;
    if (!audio || !audioReady) return;

    try {
      audio.currentTime = seekTime;
      setCurrentTime(seekTime);
    } catch (error) {

      setCurrentTime(seekTime);
    }
  };

  const handleRestart = () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      audio.pause();
      audio.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
      onPlayStateChange?.(false);
    } catch (error) {

      setCurrentTime(0);
      setIsPlaying(false);
      onPlayStateChange?.(false);
    }
  };

  const handleDownload = () => {
    try {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `voicemail-${Date.now()}.mp3`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {

      alert('Download failed. Please try again.');
    }
  };

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0] / 100;
    setVolume(newVolume);
    
    const audio = audioRef.current;
    if (audio) {
      audio.volume = newVolume;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0;

  return (
    <div className="space-y-3">
      <audio
        ref={audioRef}
        preload="none"
      />

      {!!audioError && (
        <Badge className="text-xs" variant="destructive">
          {audioError}
        </Badge>
      )}
      
      <div className="flex items-center gap-3">
        <Button
          className="shrink-0"
          disabled={loading || !!audioError}
          onClick={handlePlay}
          size="sm"
          variant="outline"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </Button>

        <Button
          className="shrink-0"
          disabled={loading || !audioReady}
          onClick={handleRestart}
          size="sm"
          variant="ghost"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>

        <div className="flex-1 space-y-1">
          {/* Waveform visualization */}
          <AudioWaveform
            currentTime={currentTime}
            duration={duration}
            isPlaying={isPlaying}
            onSeek={(time) => {
              const audio = audioRef.current;
              if (audio && audioReady) {
                audio.currentTime = time;
                setCurrentTime(time);
              }
            }}
            isLoading={loading}
            barCount={60}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Volume2 className="h-4 w-4 text-muted-foreground" />
          <Slider
            className="w-16"
            max={100}
            onValueChange={handleVolumeChange}
            step={5}
            value={[volume * 100]}
          />
          {!!showDownload && (
            <Button
              className="shrink-0"
              disabled={loading || !audioReady}
              onClick={handleDownload}
              size="sm"
              variant="ghost"
            >
              <Download className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {!!transcript && (
        <div className="space-y-2">
          <Button
            className="text-xs"
            onClick={() => setShowTranscript(!showTranscript)}
            size="sm"
            variant="ghost"
          >
            {showTranscript ? 'Hide' : 'Show'} Transcript
          </Button>
          
          {!!showTranscript && (
            <div className="bg-muted/50 rounded-lg p-3">
              <Badge className="mb-2" variant="secondary">
                Auto-generated transcript
              </Badge>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {transcript}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
